import { useCallback, useEffect } from 'react'
import { parsePriceToCents } from '@npco/component-mp-common'
import {
  CreateBpayPaymentInstrumentState,
  useCreateBpayPaymentInstrumentMFAState,
} from 'features/MFA'

import { useCreatePaymentInstrument } from 'hooks/paymentInstruments/useCreatePaymentInstrument'
import {
  MFA_ERROR,
  RESOURCE_ALREADY_EXISTS_ERROR,
  UNEXPECTED_ERROR,
} from 'types/errors'
import { useSubmitBpayTransfer } from 'pages/Transfer/hooks/useSendBpayTransfer/useSubmitBpayTransfer'
import { useTransferState } from 'pages/Transfer/Transfer.context'
import { TransferEvent } from 'pages/Transfer/Transfer.stateMachine'

export type CreatePaymentInstrumentError =
  | typeof UNEXPECTED_ERROR
  | typeof MFA_ERROR
  | typeof RESOURCE_ALREADY_EXISTS_ERROR

export const useCreateBpayTransferOnMFARedirect = () => {
  const {
    createBpayPaymentInstrumentState,
    hasRedirectedBackToApp,
    setHasRedirectedBackToApp,
  } = useCreateBpayPaymentInstrumentMFAState()
  const { createPaymentInstrument } = useCreatePaymentInstrument()

  const { submitBpayTransfer } = useSubmitBpayTransfer(5)
  const {
    transferState: [, send],
  } = useTransferState()

  const sendTransfer = useCallback(
    async (state: CreateBpayPaymentInstrumentState) => {
      const {
        isDynamicPayment,
        crn,
        createPaymentInstrumentInput,
        debitCardAccountUuid,
        amount,
      } = state.variables

      const paymentInstrumentResponse = await createPaymentInstrument(
        createPaymentInstrumentInput
      )

      // NOTE: This is incomplete, a failure screen needs to render
      if (
        paymentInstrumentResponse === MFA_ERROR ||
        paymentInstrumentResponse === UNEXPECTED_ERROR ||
        paymentInstrumentResponse === RESOURCE_ALREADY_EXISTS_ERROR
      ) {
        send(TransferEvent.Error)
        return
      }

      try {
        const submitBpayTransferResults = await submitBpayTransfer({
          isDynamicPayment,
          amountInCents: parsePriceToCents(amount).toString(),
          debitCardAccountUuid,
          paymentInstrumentUuid: paymentInstrumentResponse.id,
          crn,
        })

        if (submitBpayTransferResults === UNEXPECTED_ERROR) {
          send(TransferEvent.Error)
          return
        }

        send(TransferEvent.Continue)
      } catch (err) {
        send(TransferEvent.Error)
      }
    },
    [createPaymentInstrument, submitBpayTransfer, send]
  )

  useEffect(() => {
    if (hasRedirectedBackToApp && createBpayPaymentInstrumentState) {
      setHasRedirectedBackToApp(false)
      send(TransferEvent.TransferContactOnRedirect)
      sendTransfer(createBpayPaymentInstrumentState)
    }
  }, [
    sendTransfer,
    setHasRedirectedBackToApp,
    send,
    hasRedirectedBackToApp,
    createBpayPaymentInstrumentState,
  ])

  return {
    hasRedirectedBackToApp,
  }
}
